import { BAUSTEIN_BY_ID } from "@/modules/lernpfad/data/bausteine"
import { deriveEvidence } from "@/modules/lernpfad/data/evidence-seed"
import { DEMO_CATALOG } from "@/modules/lernpfad/data/obligations"
import { summarisePopulation } from "@/modules/lernpfad/engine/aggregate"
import type { PopulationSummary } from "@/modules/lernpfad/engine/aggregate"
import type { Constraints, EmployeeProfile } from "@/modules/lernpfad/engine"

/**
 * Client-side port of `app/api/demo/lernpfad-summary/route.ts` from the
 * ai-mb-1 monorepo this demo was extracted from. That route existed only to
 * keep the 1.7 MB seeded population off the wire — a constraint that does not
 * apply here, since this IS the wire: there is no backend to proxy through.
 * The population ships as a static `/population.json` asset instead (fetched
 * once, not bundled into the JS chunk), and aggregation — ~25-80ms over 1,960
 * people — runs in the browser on every lever change, same as it ran in the
 * Next.js server process before.
 */

interface SeededEmployee {
    id: string
    displayName: string
    managerId: string | null
    orgUnit: string
    orgUnitLabel: string
    kurzzeichen: string
    roleId: string
    roleLabel: string
    pensum: number
    yearsInField: number
    signingAuthority?: string
    completed: Array<{ bausteinId: string; completedOn: string }>
}

let profilesPromise: Promise<EmployeeProfile[]> | null = null

function loadProfiles(): Promise<EmployeeProfile[]> {
    if (!profilesPromise) {
        profilesPromise = fetch("/population.json")
            .then((response) => response.json())
            .then((population: { employees: SeededEmployee[] }) =>
                population.employees.map((employee) => ({
                    ...employee,
                    skills: [],
                    evidence: deriveEvidence(employee.id, employee.completed, BAUSTEIN_BY_ID),
                })),
            )
    }
    return profilesPromise
}

/** Stable 32-bit FNV-1a hash — mirrors data/connectors.ts. */
function fnv1a(input: string): number {
    let hash = 0x811c9dc5
    for (let index = 0; index < input.length; index++) {
        hash ^= input.charCodeAt(index)
        hash = Math.imul(hash, 0x01000193)
    }
    return hash >>> 0
}

const OBLIGATION_BAUSTEINE = new Set(DEMO_CATALOG.obligations.map((o) => o.bausteinId))

/**
 * The simulated Moodle sync: ~40% of employees (hash-picked) get every lapsed
 * MANDATORY completion re-dated to `asOf`.
 */
function withSimulatedSync(profiles: EmployeeProfile[], asOf: string): EmployeeProfile[] {
    return profiles.map((profile) => {
        if (fnv1a(`sync-${profile.id}`) % 100 >= 40) return profile
        return {
            ...profile,
            completed: profile.completed.map((entry) =>
                OBLIGATION_BAUSTEINE.has(entry.bausteinId)
                    ? { ...entry, completedOn: asOf }
                    : entry,
            ),
        }
    })
}

/** Key-sorted stringify, so two structurally equal requests hash identically. */
function canonicalKey(value: unknown): string {
    const sorted = (input: unknown): unknown => {
        if (Array.isArray(input)) return input.map(sorted)
        if (input !== null && typeof input === "object") {
            const record = input as Record<string, unknown>
            return Object.fromEntries(
                Object.keys(record)
                    .sort()
                    .map((key) => [key, sorted(record[key])]),
            )
        }
        return input
    }
    return JSON.stringify(sorted(value))
}

/** Insertion-ordered LRU over recent summaries — same shape as the server route had. */
const CACHE_LIMIT = 32
const CACHE = new Map<string, PopulationSummary>()

export interface PopulationSummaryRequest extends Constraints {
    simulateSync?: boolean
}

export async function getPopulationSummary(
    request: PopulationSummaryRequest,
): Promise<PopulationSummary> {
    const { simulateSync, ...constraints } = request

    const key = canonicalKey({ ...constraints, simulateSync })
    const hit = CACHE.get(key)
    if (hit) {
        CACHE.delete(key)
        CACHE.set(key, hit)
        return hit
    }

    const profiles = await loadProfiles()
    const withSync = simulateSync ? withSimulatedSync(profiles, constraints.asOf) : profiles
    const summary = summarisePopulation(withSync, DEMO_CATALOG, constraints)

    CACHE.set(key, summary)
    if (CACHE.size > CACHE_LIMIT) CACHE.delete(CACHE.keys().next().value as string)

    return summary
}
